<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Guidebook'"
      [canGoBack]="true"
      [customBack]="'more'" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<!-- One section card. The header toggles it open; rearranging keeps everything closed. -->
<ng-template #sectionCard let-section>
  <tidy-card
    class="gbd-card"
    [id]="'gbd-section-' + section.id"
    [class.gbd-card--internal]="section.audience === 'internal'"
    [class.gbd-card--collapsed]="!section.isExpanded"
    [class.gbd-card--rearranging]="isRearranging()"
    [class.gbd-card--busy]="section.isBusy">
    <div class="gbd-card__head" (click)="toggleCard(section)">
      @if (isRearranging() && section.draggable) {
        <tidy-image
          [src]="'assets/svg/reorder-three-outline.svg'"
          class="gbd-card__drag drag-handle-icon" />
      }
      <tidy-image [src]="section.icon" class="gbd-card__icon" />
      <div class="gbd-card__title">
        <tidy-text [header]="section.title" [translate]="false" />
        @if (section.audience === 'internal') {
          <span class="gbd-chip gbd-chip--internal">
            <span
              class="gbd-chip__icon"
              [style.-webkit-mask-image]="'url(assets/svg/lock-closed-outline.svg)'"
              [style.mask-image]="'url(assets/svg/lock-closed-outline.svg)'"></span>
            Internal
          </span>
        }
      </div>
      @if (!isRearranging() && section.menuItems.length) {
        <div class="gbd-card__menu-wrap" (click)="$event.stopPropagation()">
          <tidy-dropdown-menu
            class="gbd-card__menu"
            [triggerText]="''"
            [placeholder]="''"
            [triggerIcon]="'more_horiz'"
            [items]="section.menuItems"
            [showCheckmarks]="false"
            (selectItem$)="onMenuSelect(section, $event)" />
        </div>
      }
      @if (!isRearranging()) {
        <tidy-image
          [src]="'assets/svg/chevron-down-outline.svg'"
          class="gbd-card__chevron"
          [class.gbd-card__chevron--open]="section.isExpanded" />
      }
    </div>

    @if (section.isExpanded) {
      @if (section.text) {
        <div class="gbd-card__text">{{ section.text }}</div>
      }

      @if (section.rows.length) {
        <div class="gbd-rows">
          @for (row of section.rows; track $index) {
            <div class="gbd-row" [class.gbd-row--wide]="row.wide">
              <tidy-text [helper]="row.label" [translate]="false" />
              <div class="gbd-row__value" [class.gbd-row__value--mono]="row.mono">{{ row.value }}</div>
            </div>
          }
        </div>
      }

      @if (section.photos.length) {
        <div class="gbd-photos">
          @for (photo of section.photos; track photo.id) {
            <div class="gbd-photo">
              <tidy-image [src]="photo.url" class="gbd-photo__img" />
              @if (photo.caption) {
                <tidy-text [helper]="photo.caption" [translate]="false" />
              }
            </div>
          }
        </div>
      }

      @if (section.showEmptyHint) {
        <tidy-row>
          <tidy-text [helper]="'Nothing here yet. Choose Edit to add it.'" />
        </tidy-row>
      }

      @if (section.categoryId === 'access' && section.audience === 'guest' && addressId(); as id) {
        <guidebook-door-codes
          [addressId]="id"
          [reloadKey]="doorCodesRev()"
          (openStay)="openStay($event)"
          (generateCode)="openGenerateCode($event)" />
      }

      @if (!isDemo && addressId() && section.takesCardPhotos) {
        <guidebook-card-photos
          [cardKey]="section.id"
          [photos]="photosByCard()[section.id] || []"
          [isSaving]="savingPhotoCards().has(addressId() + ':' + section.id)"
          (addPhotos)="addCardPhotos(section.id, $event)"
          (removePhoto)="removeCardPhoto(section.id, $event)" />
      }

      @if (section.kind === 'contacts') {
        <tidy-row>
          <tidy-text [helper]="'Guests see a Contact Us card with a call button to your guest number. This is added automatically.'" />
        </tidy-row>
      }

      @if (section.isConfirmingRemove) {
        <div class="gbd-card__confirm">
          <tidy-text [body]="'Remove this section? This can’t be undone.'" />
          <div class="gbd-card__actions">
            <tidy-small-button
              [text]="'Remove'"
              [danger]="true"
              [disabled]="section.isBusy"
              (buttonClick)="confirmRemove(section)" />
            <tidy-small-button
              [text]="'Cancel'"
              (buttonClick)="cancelRemove()" />
          </div>
        </div>
      }
    }
  </tidy-card>
</ng-template>

<!-- Header actions, shared by the desktop header and the mobile action row. -->
<ng-template #actions>
  <tidy-small-button
    [text]="'Add Section'"
    [image]="'assets/svg/add-outline.svg'"
    (buttonClick)="addSection()" />
  <tidy-small-button
    [text]="'View Guest Guidebook'"
    [image]="'assets/svg/open-outline.svg'"
    (buttonClick)="openPreview()" />
</ng-template>

<!-- Rearranging only reorders the guest sections, so the toggle rides that group's row. -->
<ng-template #rearrangeToggle>
  <div class="gbd-rearrange">
    <tidy-text [smallBody]="'Rearrange'" />
    <tidy-toggle
      [toggled]="isRearranging()"
      (toggleChanged)="toggleRearrange()" />
  </div>
</ng-template>

<!-- The answer to the question in the ask bar, with links to the sections it came from. -->
<ng-template #answerCard>
  <tidy-card class="gbd-answer">
    <div class="gbd-card__head gbd-card__head--static">
      <tidy-image [src]="'assets/svg/chatbubble-ellipses-outline.svg'" class="gbd-card__icon" />
      <div class="gbd-card__title">
        <tidy-text [header]="question()" [translate]="false" />
      </div>
      <tidy-text
        [actionBody]="'Clear'"
        (action)="clearAsk()"
        class="link" />
    </div>

    @if (isAsking()) {
      <div class="gbd-answer__status">
        <ion-spinner name="lines-small" />
        <tidy-text [helper]="'Reading the guidebook…'" />
      </div>
    }

    @if (answer(); as a) {
      <div class="gbd-card__text">{{ a.text }}</div>
      @if (sourceChips().length) {
        <div class="gbd-answer__sources">
          <tidy-text [smallHelper]="'From'" />
          @for (chip of sourceChips(); track chip.id) {
            <button type="button" class="gbd-source" (click)="scrollToSection(chip.id)">{{ chip.title }}</button>
          }
        </div>
      }
    }

    @if (askError()) {
      <tidy-row>
        <tidy-text [helper]="askError()" />
      </tidy-row>
    }

    @if (!isAsking() && matchCount() === 0 && !answer()?.found) {
      <tidy-row>
        <tidy-text [helper]="'Nothing in this guidebook mentions that yet. Add a section or note so the team can find it next time.'" />
      </tidy-row>
    }
  </tidy-card>

  <div class="gbd-filter-note">
    @if (isFiltering()) {
      <tidy-text [smallHelper]="('Showing ' | translate) + matchCount() + (' matching section' | translate) + (matchCount() === 1 ? '' : 's')" />
      <tidy-text
        [actionBody]="'Show all sections'"
        (action)="showAllSections()"
        class="link" />
    } @else {
      <tidy-text [smallHelper]="'Showing all sections'" />
    }
  </div>
</ng-template>

<!-- Both guidebooks in one list: guest sections first, internal notes after a divider. -->
<ng-template #guidebookList>
  @if (isRearranging()) {
    <tidy-alert class="gbd-rearrange-hint">
      <tidy-row>
        <tidy-text [body]="'Drag sections to change the order guests see them. Welcome, check-in, WiFi and house rules always come first.'" />
      </tidy-row>
    </tidy-alert>
  }

  @if (showGuestGroup()) {
    <div class="gbd-group">
      <div class="gbd-group__label">
        <span
          class="gbd-group__icon"
          [style.-webkit-mask-image]="'url(assets/svg/globe-outline.svg)'"
          [style.mask-image]="'url(assets/svg/globe-outline.svg)'"></span>
        <tidy-text [header]="'Guest Guidebook'" class="gbd-group__title" />
        <tidy-text
          [helper]="'Shown to guests on the property website'"
          class="gbd-group__helper" />
      </div>
      <div class="gbd-group__right">
        <ng-container *ngTemplateOutlet="rearrangeToggle" />
      </div>
    </div>

    @for (section of visibleFixed(); track section.id) {
      <ng-container *ngTemplateOutlet="sectionCard; context: { $implicit: section }" />
    }

    <tidy-drag-drop
      dragDropId="guidebook-guest-sections"
      [dataList]="visibleDraggableCategories()"
      [disabled]="!isRearranging()"
      [boundary]="'tidy-wrapper'"
      (listDropped)="onSectionsReordered($event)">
      @for (section of visibleCategorySections(); track section.id) {
        <ng-container *tidyDragDropItem>
          <ng-container *ngTemplateOutlet="sectionCard; context: { $implicit: section }" />
        </ng-container>
      }
    </tidy-drag-drop>

    @for (section of visibleLegacy(); track section.id) {
      <ng-container *ngTemplateOutlet="sectionCard; context: { $implicit: section }" />
    }

    @if (visibleGuestCount() === 0) {
      <div class="gbd-group-empty">
        <tidy-text [helper]="isFiltering() ? 'No guest sections match.' : (hasGuestSections() ? 'No guest sections.' : 'Nothing shown to guests yet. Add a section to start building the website.')" />
      </div>
    }

    @if (model(); as m) {
      @if (!isFiltering() && !isRearranging()) {
        <tidy-card class="gbd-card gbd-guest-settings">
          <div class="gbd-card__head gbd-card__head--static">
            <tidy-image [src]="'assets/svg/settings-outline.svg'" class="gbd-card__icon" />
            <div class="gbd-card__title">
              <tidy-text [header]="'Guest Settings'" />
            </div>
          </div>
          <div class="gbd-rearrange gbd-guest-settings__row">
            <tidy-text [body]="'Collect guest contact info'" />
            @for (rev of [contactToggleRev()]; track rev) {
              <tidy-toggle
                [toggled]="m.contactCollectionEnabled"
                [disabled]="busyKeys().has('guest_settings')"
                (toggleChanged)="setContactCollection(!m.contactCollectionEnabled)" />
            }
          </div>
        </tidy-card>
      }
    }
  }

  @if (showInternalGroup()) {
    <div class="gbd-group gbd-group--internal" [class.gbd-group--first]="!showGuestGroup()">
      <div class="gbd-group__label">
        <span
          class="gbd-group__icon"
          [style.-webkit-mask-image]="'url(assets/svg/lock-closed-outline.svg)'"
          [style.mask-image]="'url(assets/svg/lock-closed-outline.svg)'"></span>
        <tidy-text [header]="'Internal Guidebook'" class="gbd-group__title" />
        <tidy-text
          [helper]="'Only your team and TIDY see these. Never shown to guests.'"
          class="gbd-group__helper" />
      </div>
    </div>

    @for (section of visibleInternal(); track section.id) {
      <ng-container *ngTemplateOutlet="sectionCard; context: { $implicit: section }" />
    } @empty {
      <div class="gbd-group-empty">
        <tidy-text [helper]="isFiltering() ? 'No internal notes match.' : 'No internal notes yet. Use them for playbooks, FAQs, lock details, and anything guests shouldn’t see.'" />
      </div>
    }
  }
</ng-template>

<ng-template #mainColumn>
  @if (!isLoaded()) {
    <div class="gbd-skeleton">
      <div class="gbd-group gbd-skeleton__group">
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-icon" />
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-title" />
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-helper" />
      </div>
      @for (width of skeletonGuestWidths; track $index) {
        <tidy-card class="gbd-card gbd-card--collapsed">
          <div class="gbd-card__head gbd-card__head--static">
            <ion-skeleton-text animated="true" class="gbd-skeleton__icon" />
            <ion-skeleton-text animated="true" class="gbd-skeleton__title" [style.width.px]="width" />
            <span class="gbd-skeleton__spacer"></span>
            <ion-skeleton-text animated="true" class="gbd-skeleton__dots" />
            <ion-skeleton-text animated="true" class="gbd-skeleton__chevron" />
          </div>
        </tidy-card>
      }
      <div class="gbd-group gbd-group--internal gbd-skeleton__group">
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-icon" />
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-title" />
        <ion-skeleton-text animated="true" class="gbd-skeleton__group-helper" />
      </div>
      @for (width of skeletonInternalWidths; track $index) {
        <tidy-card class="gbd-card gbd-card--collapsed gbd-card--internal">
          <div class="gbd-card__head gbd-card__head--static">
            <ion-skeleton-text animated="true" class="gbd-skeleton__icon" />
            <ion-skeleton-text animated="true" class="gbd-skeleton__title" [style.width.px]="width" />
            <span class="gbd-skeleton__spacer"></span>
            <ion-skeleton-text animated="true" class="gbd-skeleton__dots" />
            <ion-skeleton-text animated="true" class="gbd-skeleton__chevron" />
          </div>
        </tidy-card>
      }
    </div>
  }

  @if (isLoaded() && model()) {
    @if (question()) {
      <ng-container *ngTemplateOutlet="answerCard" />
    }
    <ng-container *ngTemplateOutlet="guidebookList" />
  }
</ng-template>

<ng-template #content>
  <tidy-wrapper [isPrimaryLayer]="true">
    @if (errorMessage()) {
      <tidy-error-message [text]="errorMessage()" />
    }

    @if (windowService.isDesktopRes) {
      <tidy-desktop-header>
        <div class="wrappable-container">
          <tidy-text [header]="'Guidebook'" />
          @if (propertyItems().length) {
            <tidy-header-select
              [items]="propertyItems()"
              [form]="propertyControl"
              [icon]="'assets/svg/home.svg'"
              (optionChange)="onPropertyChange($event)" />
          }
          <tidy-header-select
            [items]="viewItems"
            [form]="viewControl"
            [icon]="'assets/svg/eye-outline.svg'"
            (optionChange)="onViewChange($event)" />
          <ng-container *ngTemplateOutlet="actions" />
        </div>
        <div class="non-wrappable-right-aligned-container">
          <automations-preview-button [tag]="'guidebook'" />
        </div>
      </tidy-desktop-header>

      <div class="gbd-ask-row">
        <guidebook-ask-bar
          [placeholder]="'Ask this guidebook anything, e.g. “how do I reset the wifi?”' | translate"
          [busy]="isAsking()"
          (ask)="onAsk($event)"
          (cleared)="clearAsk()" />
      </div>

      <ng-container *ngTemplateOutlet="mainColumn" />
    }

    @if (!windowService.isDesktopRes) {
      @if (propertyItems().length) {
        <tidy-row>
          <tidy-select
            [form]="propertyControl"
            [items]="propertyItems()"
            [icon]="'assets/svg/home.svg'"
            (optionChange)="onPropertyChange($event)" />
        </tidy-row>
      }
      <tidy-row>
        <tidy-select
          [form]="viewControl"
          [items]="viewItems"
          [icon]="'assets/svg/eye-outline.svg'"
          (optionChange)="onViewChange($event)" />
      </tidy-row>
      <div class="gbd-ask-row">
        <guidebook-ask-bar
          [placeholder]="'Ask this guidebook…' | translate"
          [busy]="isAsking()"
          (ask)="onAsk($event)"
          (cleared)="clearAsk()" />
      </div>
      <div class="gbd-mobile-actions">
        <ng-container *ngTemplateOutlet="actions" />
      </div>
      <ng-container *ngTemplateOutlet="mainColumn" />
    }
  </tidy-wrapper>
</ng-template>

<app-scroll-wrapper
  [isfullWidthContent]="true"
  [templateContent]="content" />

@if (showAboutThisPage) {
  <tidy-about-this-page [page]="'guidebook'" (dismissed)="dismissAboutThisPage()" />
}
